import {
  MediaGridView,
  MediaGridViewProps,
} from '@euler/app/flows/common/components/MediaGridView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useBehaviorSubject, useContextBridge } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useContext, useEffect, useMemo } from 'react';
import {
  VehicleMediaQueryParams,
  useServiceRecordMedias,
} from '../../functions/useServiceRecordMedias';
import { SelectMedias } from './components/SelectMedias';
import { MediasPageContext, MediasPageProvider } from './context';
import { getMediasPageTitleByQueryParams } from './functions/utils';

export const ServiceRecordMediasPage = wrapNavigatorScreen(
  (props: VehicleMediaQueryParams) => {
    const { state, loading, refreshing, error, invalidate } =
      useServiceRecordMedias(props);

    const data = useMemo(() => state ?? [], [state]);

    return (
      <MediasPageProvider queryParams={props} data={data}>
        <Content
          data={data}
          loading={loading}
          refreshing={refreshing}
          error={error}
          onRefresh={invalidate}
        />
      </MediasPageProvider>
    );
  },
  ({ route }) => ({
    title: getMediasPageTitleByQueryParams(route.params),
  }),
);

const Content = memo((props: MediaGridViewProps) => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const ContextBridge = useContextBridge(MediasPageContext);

  const isMobile = useIsMobileLayout();
  const { rpx } = useResponsiveContext();

  const { isSelectionModeActive$, queryParams, select, deselect, selection } =
    useContext(MediasPageContext);

  const [isSelectionModeActive] = useBehaviorSubject(isSelectionModeActive$);

  useEffect(() => {
    if (!isMobile) return;
    navigation.setOptions({
      title: isSelectionModeActive
        ? ''
        : getMediasPageTitleByQueryParams(queryParams),
      headerBackTitle: isSelectionModeActive
        ? getMediasPageTitleByQueryParams(queryParams)
        : '',
      headerBackTitleVisible: isSelectionModeActive,
    });
  }, [isMobile, isSelectionModeActive, navigation, queryParams]);

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <ContextBridge>
          <SelectMedias style={{ marginRight: 15 }} />
        </ContextBridge>
      ),
    });
    return () => {
      navigation.setOptions({ headerRight: undefined });
    };
  }, [ContextBridge, navigation]);

  return (
    <MediaGridView
      {...props}
      checkIconSize={rpx(isMobile ? 20 : 28)}
      mode={isSelectionModeActive ? 'select' : 'view'}
      selection={isSelectionModeActive ? selection : undefined}
      onMediaSelected={select}
      onMediaDeselected={deselect}
    />
  );
});
